/**
 * ResearchCharts.jsx — the three Overview charts, as inline SVG.
 * =============================================================================
 * All three are SINGLE-SERIES by design (dataviz: "emphasis", not categorical).
 * The only second colour is a status state — "too few trades to tell edge from
 * luck" — and it is never carried by colour alone: those points are hollow
 * rings inside a shaded, labelled zone, and the tooltip spells it out.
 *
 * Colour check (run, not eyeballed): accent #C9F24A and warn #F2B84B on the
 * #121513 panel measure 13.6:1 and 9.8:1 contrast, with deuteranopia ΔE 8.4 and
 * normal-vision ΔE 15.5 — above the ≥8 target and the ≥15 hard floor. The
 * categorical lightness band does not apply here; this is emphasis + status,
 * and snapping to it would have made the brand lime a dark olive.
 *
 * Grid and axes are solid hairlines one shade off the surface. No dashed grid,
 * no value printed on every point, no dual axis.
 */

import React, { useId, useState } from "react";

import { fmtCompact, fmtInt, fmtPct } from "../../lib/format";

const ACCENT = "#C9F24A";
const WARN = "#F2B84B";
const GRID = "#1E2420";
const AXIS = "#242A26";
const MUTED = "#9AA39B";
const FAINT = "#7E877E";
const SURFACE = "#121513";

/** Shared tooltip. Positioned in SVG user space, clamped to the plot. */
function Tip({ x, y, width, lines, plotW }) {
  const w = Math.max(...lines.map((l) => l.length)) * 6.0 + 18;
  const h = lines.length * 14 + 12;
  const left = Math.min(Math.max(4, x - w / 2), plotW - w - 4);
  const top = Math.max(4, y - h - 12);
  return (
    <g pointerEvents="none">
      <rect x={left} y={top} width={w} height={h} rx="8"
            fill="#0B0D0C" stroke={AXIS} />
      {lines.map((l, i) => (
        <text key={i} x={left + 9} y={top + 17 + i * 14}
              fill={i === 0 ? "#E8EAE3" : MUTED}
              fontSize={i === 0 ? 11.5 : 10.5}
              fontFamily={i === 0 ? "inherit" : "var(--font-mono)"}>
          {l}
        </text>
      ))}
    </g>
  );
}

function EmptyPlot({ height, children }) {
  return (
    <div className="rx-empty" style={{ minHeight: height, display: "flex", alignItems: "center" }}>
      {children}
    </div>
  );
}

/* ── 1. Edge vs sample size ────────────────────────────────────────────────── */

export function EdgeVsSampleChart({ records, height = 300 }) {
  const [hover, setHover] = useState(null);
  const clipId = useId();

  const pts = (records || [])
    .filter((r) => (r.backtest?.trades || 0) > 0 && r.backtest?.net_per_trade_pct != null);

  if (!pts.length) {
    return (
      <EmptyPlot height={height}>
        No promoted strategy has both a trade count and a net-per-trade figure
        yet, so there is nothing to plot.
      </EmptyPlot>
    );
  }

  const W = 640, H = height;
  const M = { t: 14, r: 16, b: 38, l: 46 };
  const pw = W - M.l - M.r, ph = H - M.t - M.b;

  const XMIN = 20, XMAX = 2000, YMIN = -0.5, YMAX = 4;
  const lx = (v) => Math.log10(Math.min(XMAX, Math.max(XMIN, v)));
  const x = (v) => M.l + ((lx(v) - lx(XMIN)) / (lx(XMAX) - lx(XMIN))) * pw;
  const y = (v) => M.t + (1 - (Math.min(YMAX, Math.max(YMIN, v)) - YMIN) / (YMAX - YMIN)) * ph;

  const THIN = 100;
  const thinCount = pts.filter((r) => r.backtest.trades < THIN).length;

  const xTicks = [20, 50, 100, 200, 500, 1000, 2000];
  const yTicks = [0, 1, 2, 3, 4];

  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
           aria-label={`Net return per trade against backtest trade count for ${pts.length} strategies. ${thinCount} have fewer than ${THIN} trades.`}>
        <defs>
          <clipPath id={clipId}>
            <rect x={M.l} y={M.t} width={pw} height={ph} />
          </clipPath>
        </defs>

        {/* The zone where sample size cannot distinguish edge from luck. */}
        <g clipPath={`url(#${clipId})`}>
          <rect x={M.l} y={M.t} width={x(THIN) - M.l} height={ph}
                fill={WARN} opacity="0.07" />
          <line x1={x(THIN)} y1={M.t} x2={x(THIN)} y2={M.t + ph}
                stroke={WARN} strokeWidth="1" opacity="0.5" />
        </g>
        <text x={M.l + 8} y={M.t + 16} fill={WARN} fontSize="10.5" fontWeight="600">
          Too few trades to tell edge from luck
        </text>
        <text x={M.l + 8} y={M.t + 30} fill={MUTED} fontSize="10"
              fontFamily="var(--font-mono)">
          {thinCount} of {pts.length}
        </text>

        {/* Grid — solid hairlines, one shade off the surface. */}
        {yTicks.map((t) => (
          <line key={t} x1={M.l} y1={y(t)} x2={M.l + pw} y2={y(t)}
                stroke={GRID} strokeWidth="1" />
        ))}
        {/* Zero is the meaningful baseline: below it the strategy loses money. */}
        <line x1={M.l} y1={y(0)} x2={M.l + pw} y2={y(0)} stroke={AXIS} strokeWidth="1" />

        {xTicks.map((t) => (
          <text key={t} x={x(t)} y={H - 14} fill={FAINT} fontSize="10"
                textAnchor="middle" fontFamily="var(--font-mono)">
            {fmtCompact(t)}
          </text>
        ))}
        {yTicks.map((t) => (
          <text key={t} x={M.l - 8} y={y(t) + 3.5} fill={FAINT} fontSize="10"
                textAnchor="end" fontFamily="var(--font-mono)">
            {t}%
          </text>
        ))}
        <text x={M.l + pw / 2} y={H - 1} fill={MUTED} fontSize="10.5" textAnchor="middle">
          Backtest trades (log scale)
        </text>

        <g clipPath={`url(#${clipId})`}>
          {pts.map((r, i) => {
            const thin = r.backtest.trades < THIN;
            const cx = x(r.backtest.trades), cy = y(r.backtest.net_per_trade_pct);
            const on = hover?.key === r.key;
            return (
              <circle
                key={r.key || i}
                cx={cx} cy={cy} r={on ? 6.5 : 4.5}
                fill={thin ? "none" : ACCENT}
                stroke={thin ? WARN : SURFACE}
                strokeWidth={thin ? 1.5 : 2}   /* 2px surface ring on overlap */
                opacity={hover && !on ? 0.45 : 0.95}
                onMouseEnter={() => setHover(r)}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: "pointer" }}
              />
            );
          })}
        </g>

        {hover && (
          <Tip
            x={x(hover.backtest.trades)} y={y(hover.backtest.net_per_trade_pct)}
            plotW={W}
            lines={[
              (hover.name || "unnamed").slice(0, 38),
              `${fmtInt(hover.backtest.trades)} trades · ${fmtPct(hover.backtest.net_per_trade_pct)}/trade`,
              `${fmtInt(hover.backtest.profitable_pairs)} profitable pairs`,
              hover.author ? `by ${hover.author}` : "author unknown",
            ]}
          />
        )}
      </svg>
      <figcaption className="rx-faint" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
        One point per strategy. Filled = {THIN}+ trades. Hollow amber ring = fewer
        than {THIN}, inside the shaded zone.
      </figcaption>
    </figure>
  );
}

/* ── 2. Where promoted strategies come from ───────────────────────────────── */

export function AuthorBars({ records, height = 260 }) {
  const rows = React.useMemo(() => {
    const by = new Map();
    (records || []).forEach((r) => {
      const a = r.author || "unknown";
      if (!by.has(a)) by.set(a, []);
      by.get(a).push(r.backtest?.trades || 0);
    });
    return Array.from(by.entries())
      .map(([author, trades]) => {
        const sorted = [...trades].sort((x, z) => x - z);
        const mid = Math.floor(sorted.length / 2);
        const median = sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
        return { author, count: trades.length, median };
      })
      .sort((a, b) => b.count - a.count);
  }, [records]);

  if (!rows.length) {
    return <EmptyPlot height={height}>No promoted strategies to group by author.</EmptyPlot>;
  }

  const max = Math.max(...rows.map((r) => r.count));
  return (
    <div>
      {rows.map((r) => (
        <div key={r.author} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 5 }}>
            <span style={{ fontSize: 12, color: "var(--text-2)", overflow: "hidden",
                           textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.author}
            </span>
            <span className="rx-num rx-faint" style={{ fontSize: 11, flexShrink: 0 }}>
              {fmtInt(r.count)} · median {fmtInt(r.median)} trades
            </span>
          </div>
          {/* One series, one colour — never a value-ramp on nominal categories. */}
          <div style={{ height: 8, background: GRID, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${(r.count / max) * 100}%`, height: "100%",
                          background: ACCENT, borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── 3. Backtest vs forward ────────────────────────────────────────────────── */

export function BacktestVsForwardChart({ records, minForward = 30, height = 260 }) {
  const [hover, setHover] = useState(null);
  const withFwd = (records || []).filter(
    (r) => (r.forward?.trades || 0) >= minForward
      && r.forward?.net_per_trade_pct != null
      && r.backtest?.net_per_trade_pct != null,
  );
  const promoted = (records || []).length;

  if (!withFwd.length) {
    const started = (records || []).filter((r) => (r.forward?.trades || 0) > 0).length;
    return (
      <EmptyPlot height={height}>
        <div>
          <strong style={{ color: "var(--text-2)" }}>
            0 of {fmtInt(promoted)} strategies have a forward test to compare yet.
          </strong>
          <div style={{ marginTop: 6 }}>
            {started
              ? `${started} have started forward-testing but none has reached ${minForward} trades, the point at which live results mean anything.`
              : `Nothing has been forward-tested on live prices, so there is no live result to plot against the backtest.`}
          </div>
        </div>
      </EmptyPlot>
    );
  }

  const W = 360, H = height;
  const M = { t: 14, r: 14, b: 34, l: 42 };
  const pw = W - M.l - M.r, ph = H - M.t - M.b;
  const all = withFwd.flatMap((r) => [r.backtest.net_per_trade_pct, r.forward.net_per_trade_pct]);
  const lo = Math.min(0, ...all), hi = Math.max(1, ...all);
  const sx = (v) => M.l + ((v - lo) / (hi - lo)) * pw;
  const sy = (v) => M.t + (1 - (v - lo) / (hi - lo)) * ph;

  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
           aria-label="Forward net per trade against backtest net per trade. Points below the diagonal did worse live than in backtest.">
        <line x1={M.l} y1={M.t + ph} x2={M.l + pw} y2={M.t + ph} stroke={AXIS} />
        <line x1={M.l} y1={M.t} x2={M.l} y2={M.t + ph} stroke={AXIS} />
        {/* y = x: on it, the edge held exactly. */}
        <line x1={sx(lo)} y1={sy(lo)} x2={sx(hi)} y2={sy(hi)} stroke={FAINT} strokeWidth="1" />
        <text x={sx(hi) - 4} y={sy(hi) + 14} fill={FAINT} fontSize="10" textAnchor="end">
          edge held
        </text>
        {withFwd.map((r, i) => {
          const on = hover?.key === r.key;
          return (
            <circle key={r.key || i}
                    cx={sx(r.backtest.net_per_trade_pct)} cy={sy(r.forward.net_per_trade_pct)}
                    r={on ? 6.5 : 4.5} fill={ACCENT} stroke={SURFACE} strokeWidth="2"
                    opacity={hover && !on ? 0.45 : 0.95}
                    onMouseEnter={() => setHover(r)} onMouseLeave={() => setHover(null)}
                    style={{ cursor: "pointer" }} />
          );
        })}
        <text x={M.l + pw / 2} y={H - 2} fill={MUTED} fontSize="10.5" textAnchor="middle">
          Backtest %/trade
        </text>
        {hover && (
          <Tip x={sx(hover.backtest.net_per_trade_pct)} y={sy(hover.forward.net_per_trade_pct)}
               plotW={W}
               lines={[
                 (hover.name || "unnamed").slice(0, 30),
                 `backtest ${fmtPct(hover.backtest.net_per_trade_pct)}`,
                 `forward  ${fmtPct(hover.forward.net_per_trade_pct)}`,
               ]} />
        )}
      </svg>
      <figcaption className="rx-faint" style={{ fontSize: 11, marginTop: 6 }}>
        Points below the line did worse live than in backtest.
      </figcaption>
    </figure>
  );
}
